import { createHttpHandlerDecorator, Reflector } from 'honorestjs'
import type { Endpoint } from '@honorest/contract'
import { ContractInterceptor } from '../interceptors'

/**
 * Metadata key for storing contract endpoints
 * Created using Reflector.createDecorator for type-safe metadata retrieval
 */
export const ContractMetadata = Reflector.createDecorator<Endpoint>({
	key: 'contract:endpoint'
})

/**
 * Decorator for contract-first API endpoints
 *
 * This decorator enables contract-first API development by:
 * - Registering the route with method and path from the contract
 * - Storing the contract endpoint metadata for validation
 * - Automatically applying the ContractInterceptor for request/response validation
 *
 * Unlike traditional HTTP decorators (@Get, @Post, etc.), this decorator accepts
 * a contract endpoint definition that contains both the HTTP method and path,
 * along with validation schemas for params, query, body, headers, and response.
 *
 * @param endpoint - The contract endpoint definition containing method, path, and schemas
 * @returns A method decorator that registers the route and enables validation
 *
 * @example
 * ```typescript
 * import { Contract } from '@honorest/contract-plugin'
 * import { UsersContract } from './contracts/users.contract'
 *
 * @Controller('/users')
 * export class UsersController {
 *   @Contract(UsersContract.endpoints.getUser)
 *   async getUser(@Param('id') id: string) {
 *     return this.userService.findById(id)
 *   }
 * }
 * ```
 */
export const Contract = (endpoint: Endpoint) => {
  const decorator = createHttpHandlerDecorator<Endpoint>({
    // Extract HTTP method from endpoint
    extractMethod: (endpoint) => {
      console.log('[Contract] extractMethod called with:', endpoint)
      return endpoint.method
    },

    // Extract path from endpoint
    extractPath: (endpoint) => {
      console.log('[Contract] extractPath called with:', endpoint)
      return endpoint.path
    },

    // Attach metadata and interceptor
    attachMetadata: (endpoint, target, propertyKey, descriptor) => {
      console.log('[Contract] attachMetadata called')
      // Store endpoint metadata using Reflector pattern
      ContractMetadata(endpoint)(target, propertyKey, descriptor)

      // Get existing interceptors metadata (if any)
      const existingInterceptors: unknown[] =
        Reflect.getMetadata('interceptor', target.constructor.prototype, propertyKey) || []

      // Add ContractInterceptor if not already present
      const hasContractInterceptor = existingInterceptors.some((interceptor) => {
        return interceptor === ContractInterceptor || interceptor?.constructor === ContractInterceptor
      })

      if (!hasContractInterceptor) {
        // Add ContractInterceptor to the list
        Reflect.defineMetadata(
          'interceptor',
          [...existingInterceptors, ContractInterceptor],
          target.constructor.prototype,
          propertyKey
        )
      }
    }
  })
  
  return decorator(endpoint) as MethodDecorator
}
